import { BillingAccountBlock } from "@/admin/accounts/BillingAccountBlock.tsx";
import { ActionButtons, type ActionItem } from "@/admin/ActionItem.tsx";
import { AdminLinkButton } from "@/admin/AdminLinkButton.tsx";
import { Extra, YesNo } from "@/admin/AdminTable.tsx";
import { gf, type GqlFieldsOf } from "@/admin/FieldConfig.tsx";
import { BooleanComponentFilter, TextFilter, UnixDateRangeFilter } from "@/admin/filters.tsx";
import { DeleteFloatingIpModalContent } from "@/admin/floating_ips/DeleteFloatingIpBlock.tsx";
import { FloatingDetailView } from "@/admin/floating_ips/FloatingDetailView.tsx";
import { FloatingToolbar } from "@/admin/floating_ips/FloatingToolbar.tsx";
import { unassignAction } from "@/admin/floating_ips/unassignAction.tsx";
import { gql } from "@/admin/graphql.gen/gql.ts";
import { IpAddressOrderFields, OrderDirection, type GetIpAddressListQuery } from "@/admin/graphql.gen/graphql.ts";
import { extractData } from "@/admin/graphql/extractData.tsx";
import { GraphqlTable } from "@/admin/GraphqlTable.tsx";
import { adminLoadBalancerLink, adminServiceLink, adminVmLink } from "@/admin/itemLinks.ts";
import { useCursorPager } from "@/admin/pager.ts";
import {
    baField,
    baIdField,
    createdAtField,
    locationField,
    userIdField,
} from "@/admin/table_fields/standardFields.tsx";
import { DISABLED } from "@/components/Action.tsx";
import { showModal } from "@/components/modal/registerModal.tsx";
import { getResourceTypeName } from "@/modules/api/resourceTypes.tsx";
import { ipAddressResourceTypeToType } from "@/modules/network/ipAddress/resourceId.ts";
import { useQuery } from "@apollo/client/react";
import { apiUnixToDate } from "@warrenio/api-spec/conversion";
import type { IpAddressResourceType } from "@warrenio/api-spec/spec.oats.gen";
import { assertNotNull, notNull } from "@warrenio/utils/notNull";

const GET_IP_ADDRESS_LIST = gql(/* GraphQL */ `
    query GetIpAddressList(
        $limit: Int
        $orderField: IpAddressOrderFields
        $orderDir: OrderDirection
        $cursor: String
        $page: Int
        $locations: [String!]
        $search: String
        $filters: [IpAddressFilter!]
    ) {
        admin_ip_address_list(
            limit: $limit
            orderField: $orderField
            orderDir: $orderDir
            cursor: $cursor
            page: $page
            locations: $locations
            search: $search
            filters: $filters
        ) {
            items {
                uuid
                user_id
                billing_account_id
                address
                is_ipv6
                name
                created_at
                updated_at
                assigned_to
                assigned_to_resource_type
                unassigned_at
                _location
                billing_account_object {
                    ...BillingAccountBlock
                }
            }
            paging {
                total
                cursor
            }
        }
    }
`);

export type GQIpAddressItem = NonNullable<GetIpAddressListQuery["admin_ip_address_list"]["items"]>[number];

export type IpAddressAssignedTo = Pick<GQIpAddressItem, "assigned_to" | "assigned_to_resource_type">;

export function AssignedToItem({ item, compact = false }: { item: IpAddressAssignedTo; compact?: boolean }) {
    const { assigned_to, assigned_to_resource_type } = item;

    if (!assigned_to_resource_type) {
        return <span className="text-muted">{compact ? "-" : "Unassigned"}</span>;
    } else {
        assertNotNull(assigned_to);
    }

    // TODO: Fix GraphQL definition to be an enum
    const resourceType = ipAddressResourceTypeToType(assigned_to_resource_type as IpAddressResourceType);
    const link = (() => {
        switch (resourceType) {
            case "managed_service":
                return adminServiceLink(assigned_to);
            case "virtual_machine":
                return adminVmLink(assigned_to);
            case "load_balancer":
                return adminLoadBalancerLink(assigned_to);
        }
    })();

    return (
        <AdminLinkButton
            action={link}
            label={<Extra value={getResourceTypeName(resourceType)}>{item.assigned_to}</Extra>}
        />
    );
}

const ipAddressFields: GqlFieldsOf<GQIpAddressItem, IpAddressOrderFields> = [
    gf({
        id: "uuid",
        title: "UUID",
        get: (a) => a.uuid,
        copyable: true,
        order: IpAddressOrderFields.Uuid,
    }),
    gf({
        ...userIdField,
        get: (a) => a.user_id,
        order: IpAddressOrderFields.UserId,
    }),
    gf({
        ...baIdField,
        get: (a) => a.billing_account_id,
        order: IpAddressOrderFields.BillingAccountId,
    }),
    gf({
        ...baField,
        get: (a) => a.billing_account_object,
        render: BillingAccountBlock,
    }),
    gf({
        ...createdAtField,
        get: (a) => (a.created_at ? apiUnixToDate(a.created_at as number) : undefined),
        order: IpAddressOrderFields.CreatedAt,
    }),
    gf({
        id: "address",
        title: "Address",
        get: (a) => a.address,
        copyable: true,
        order: IpAddressOrderFields.Address,
        filter: TextFilter,
    }),
    gf({
        id: "is_ipv6",
        title: "Is IPv6",
        get: (a) => a.is_ipv6,
        render: YesNo,
        order: IpAddressOrderFields.IsIpv6,
        filter: () => <BooleanComponentFilter component={YesNo} />,
    }),
    gf({
        id: "name",
        title: "Name",
        get: (a) => a.name,
        order: IpAddressOrderFields.Name,
        filter: TextFilter,
    }),
    gf({
        id: "assigned_to",
        title: "Assigned To",
        get: (a) => a.assigned_to,
        render: AssignedToItem,
    }),
    gf({
        id: "unassigned_at",
        title: "Unassigned At",
        get: (a) => (a.unassigned_at ? apiUnixToDate(a.unassigned_at as number) : undefined),
        order: IpAddressOrderFields.UnassignedAt,
        filter: UnixDateRangeFilter,
    }),
    gf({
        ...locationField,
        get: (a) => a._location,
    }),
];

function actions(item: GQIpAddressItem): ActionItem[] {
    return [
        unassignAction(
            !item.is_ipv6 && item.assigned_to
                ? {
                      address: item.address,
                      location: notNull(item._location),
                  }
                : null,
        ),
        {
            id: "delete_ip",
            title: "Delete",
            icon: "jp-trash-icon",
            action: item.is_ipv6 ? DISABLED : () => showModal(<DeleteFloatingIpModalContent item={item} />),
        },
    ];
}

export function FloatingIpsTable() {
    return (
        <GraphqlTable
            title="IP addresses"
            fields={ipAddressFields}
            defaults={{ orderField: IpAddressOrderFields.CreatedAt, orderDir: OrderDirection.Desc }}
            getId={(item) => item.uuid!}
            usePager={useCursorPager}
            useQuery={(variables) => {
                // eslint-disable-next-line react-hooks/rules-of-hooks
                const q = useQuery(GET_IP_ADDRESS_LIST, { variables });
                return extractData(q, (d) => d.admin_ip_address_list);
            }}
            actionButtons={(item) => <ActionButtons actions={actions(item)} inTable />}
            renderDetailToolbar={(item) => <FloatingToolbar item={item} />}
            renderDetail={(item) => <FloatingDetailView key={item.uuid} item={item} />}
        />
    );
}
